export type AcceptingStatus = "yes" | "urgent_only" | "no";
export type WaitBucket = "under_2w" | "w2_6" | "w6_12" | "over_3m";
export type ReferralMethod =
  | "healthlink"
  | "medical_objects"
  | "fax"
  | "email"
  | "portal"
  | "phone"
  | "other";
export type AvailabilityFreshness = "fresh" | "aging" | "stale";

export interface Availability {
  accepting: AcceptingStatus;
  waitBucket: WaitBucket | null;
  referralMethod: ReferralMethod | null;
  referralEmail: string | null;
  referralFax: string | null;
  referralNotes: string | null;
  telehealth: boolean | null;
  bulkBill: boolean | null;
  confirmedAt: string;
  confirmedByRole: string;
  freshness: AvailabilityFreshness;
}

export interface PracticeGroupSummary {
  id: string;
  name: string;
}

export const ACCEPTING_LABELS: Record<AcceptingStatus, string> = {
  yes: "Accepting referrals",
  urgent_only: "Urgent only",
  no: "Not accepting",
};

export const WAIT_BUCKET_LABELS: Record<WaitBucket, string> = {
  under_2w: "<2 wks",
  w2_6: "2–6 wks",
  w6_12: "6–12 wks",
  over_3m: "3+ months",
};

export const REFERRAL_METHOD_LABELS: Record<ReferralMethod, string> = {
  healthlink: "HealthLink",
  medical_objects: "Medical Objects",
  fax: "Fax",
  email: "Email",
  portal: "Portal",
  phone: "Phone",
  other: "Other",
};

export const WAIT_BUCKET_OPTIONS = typedEntries(WAIT_BUCKET_LABELS);
export const REFERRAL_METHOD_OPTIONS = typedEntries(REFERRAL_METHOD_LABELS);

export function acceptingLabel(value: AcceptingStatus): string {
  return ACCEPTING_LABELS[value];
}

export function waitBucketLabel(value: WaitBucket | null | undefined): string | null {
  return value ? WAIT_BUCKET_LABELS[value] : null;
}

export function referralMethodLabel(
  value: ReferralMethod | null | undefined,
): string | null {
  return value ? REFERRAL_METHOD_LABELS[value] : null;
}

export function availabilityFreshnessCopy(
  availability: Pick<Availability, "confirmedAt" | "freshness">,
  now = new Date(),
): string {
  const relative = formatAvailabilityRelativeTime(availability.confirmedAt, now);

  if (availability.freshness === "fresh") {
    return `Confirmed by practice · ${relative}`;
  }

  if (availability.freshness === "aging") {
    return `Confirmed ${relative}`;
  }

  return `Confirmed ${relative} — may be out of date`;
}

export function formatAvailabilityRelativeTime(iso: string, now = new Date()): string {
  const confirmedAt = new Date(iso);
  if (Number.isNaN(confirmedAt.getTime())) return "date unavailable";

  const diffDays = Math.max(
    0,
    Math.floor((now.getTime() - confirmedAt.getTime()) / 86_400_000),
  );
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 14) return `${diffDays} days ago`;

  const weeks = Math.floor(diffDays / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

export function formatAvailabilityDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function typedEntries<T extends string>(labels: Record<T, string>): Array<{
  value: T;
  label: string;
}> {
  return (Object.entries(labels) as Array<[T, string]>).map(([value, label]) => ({
    value,
    label,
  }));
}
